/// <amd-module name="Feed/Legacy/DataServices/FilterByEnabledProtocolDataService" />
import { Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { DataProviderConstants, FeedServiceInstanceId } from "Feed/Common/Constants/Constants";
import { Package } from "Feed/Common/Contracts/Feed";
import {
    PackageDetailsResult,
    PackageDetailsRetrievalResult,
    PackagesResult,
    PackagesRetrievalResult
} from "Feed/Common/Contracts/WebPage";
import * as PackageResources from "Feed/Common/Resources";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { RecycleBinDataService } from "Feed/Legacy/DataServices/RecycleBinDataService";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { getFullyQualifiedFeedId } from "Feed/Legacy/Helpers/FeedNameResolver";
import { ProtocolProvider } from "Feed/Legacy/Protocols/ProtocolProvider";

import { Feed } from "../WebApi/VSS.CustomFeed.Contracts";

export class FilterByEnabledProtocolDataService extends VssService {
    private get ProtocolProvider(): ProtocolProvider {
        return this.pageContext.getService<ProtocolProvider>("IFeedProtocolProvider");
    }

    private get HubWebPageDataService(): HubWebPageDataService {
        return this.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    }

    private get RecycleBinDataService(): RecycleBinDataService {
        return this.pageContext.getService<RecycleBinDataService>("IFeedRecycleBinDataService");
    }

    private get FeedDataService(): IFeedDataService {
        return this.pageContext.getService<IFeedDataService>("IFeedDataService");
    }

    public getPackagesResult(): PackagesResult {
        const packagesResult = this.HubWebPageDataService.getInitialPackagesResult();
        if (packagesResult.result === PackagesRetrievalResult.NoPackages) {
            return packagesResult;
        }
        packagesResult.packages = this.filterByEnabledProtocol(packagesResult.packages);
        return packagesResult;
    }

    public getRecycleBinPackagesResult(): PackagesResult {
        const packagesResult = this.RecycleBinDataService.getInitialPackagesResult();
        if (packagesResult.result === PackagesRetrievalResult.NoPackages) {
            return packagesResult;
        }
        packagesResult.packages = this.filterByEnabledProtocol(packagesResult.packages);
        return packagesResult;
    }

    public getPackageDetailsResult(): PackageDetailsResult {
        const packageDetailsResult = this.HubWebPageDataService.getInitialPackageDetailsResult();

        if (!packageDetailsResult) {
            return packageDetailsResult;
        }

        if (packageDetailsResult.result !== PackageDetailsRetrievalResult.Success) {
            return packageDetailsResult;
        }

        // check if specified protocol is disabled, feed service doesn't filter packages based on disabled protocol
        if (this.ProtocolProvider.get(packageDetailsResult.package.protocolType) !== null) {
            return packageDetailsResult;
        } else {
            packageDetailsResult.result = PackageDetailsRetrievalResult.NotFound;
            packageDetailsResult.package = null;
            return packageDetailsResult;
        }
    }

    public getRecycleBinPackageDetailsResult(): PackageDetailsResult {
        const packageDetailsResult = this.RecycleBinDataService.getInitialPackageDetailsResult();

        if (!packageDetailsResult) {
            return packageDetailsResult;
        }

        if (packageDetailsResult.result !== PackageDetailsRetrievalResult.Success) {
            return packageDetailsResult;
        }

        // check if specified protocol is disabled, feed service doesn't filter packages based on disabled protocol
        if (this.ProtocolProvider.get(packageDetailsResult.package.protocolType) !== null) {
            return packageDetailsResult;
        } else {
            packageDetailsResult.result = PackageDetailsRetrievalResult.NotFound;
            packageDetailsResult.package = null;
            return packageDetailsResult;
        }
    }

    public getRecycleBinPackagesAsync(
        feed: Feed,
        top: number,
        skip: number,
        packageNameQuery: string = null,
        protocolType?: string,
        includeAllVersions: boolean = false
    ): Promise<Package[]> {
        return this.RecycleBinDataService.getPackagesAsync(
            feed,
            protocolType,
            top,
            skip,
            packageNameQuery,
            includeAllVersions
        ).then((packages: Package[]) => {
            return this.filterByEnabledProtocol(packages);
        });
    }

    public async getPackagesAsync(
        feed: Feed,
        top: number,
        skip: number,
        includeDeleted: boolean,
        isListed: boolean,
        packageNameQuery: string = null,
        includeDescription: boolean = false
    ): Promise<Package[]> {
        if (this.HubWebPageDataService.isFeedPackagesDataProviderEnabled()) {
            const properties = {
                feed: getFullyQualifiedFeedId(feed),
                directUpstreamSourceId: feed.upstreamSource ? feed.upstreamSource.id : null,
                projectId: feed.project ? feed.project.id : null,
                top: top,
                skip: skip,
                includeDeleted: includeDeleted,
                isListed: isListed,
                packageNameQuery: packageNameQuery,
                includeDescription: includeDescription
            };

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            const packages = await contributionService.getDataAsync<Package[]>(
                DataProviderConstants.FeedPackagesDataProvider,
                properties,
                true /* ignoreCache */,
                FeedServiceInstanceId
            );
            if (!packages) {
                // we should get an empty list for the no package case, if we get undefined, there was an error:
                throw new Error(PackageResources.ErrorRetrievingPackages);
            }
            return this.filterByEnabledProtocol(packages);
        } else {
            return this.FeedDataService.getPackagesAsync(
                feed,
                null, // protocolType
                top,
                skip,
                includeDeleted,
                isListed,
                packageNameQuery,
                includeDescription
            ).then((packages: Package[]) => {
                return this.filterByEnabledProtocol(packages);
            });
        }
    }

    private filterByEnabledProtocol(packages: Package[]): Package[] {
        if (packages == null) {
            return;
        }

        // If protocol is not disabled, just return all the packages
        if (this.HubWebPageDataService.isIvyUIEnabled()) {
            return packages;
        }

        return packages.filter(pkg => {
            return this.ProtocolProvider.get(pkg.protocolType) !== null;
        });
    }
}

Services.add("IFeedFilterByEnabledProtocolDataService", { serviceFactory: FilterByEnabledProtocolDataService });
